import {formatDisplayDate as sharedFormatDisplayDate} from '@chekin/shared/guest-form';
import {formatClockLabel, formatClockTime} from '@chekin/shared/home';

const DISPLAY_LOCALE_FALLBACK = 'en-GB';

export const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

export const WEEKDAY_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
};

function toDisplayLocale(language?: string) {
  if (!language) return DISPLAY_LOCALE_FALLBACK;
  return language.startsWith('en') ? DISPLAY_LOCALE_FALLBACK : language;
}

const formatDisplayDate = sharedFormatDisplayDate;

export type {ClockTime} from '@chekin/shared/home';

function formatDayAndTime(value?: string | Date | null, language?: string) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const startOfDay = (of: Date) =>
    new Date(of.getFullYear(), of.getMonth(), of.getDate()).getTime();
  const dayOffset = Math.round(
    (startOfDay(date) - startOfDay(new Date())) / (24 * 60 * 60 * 1000),
  );

  const build = (locale: string) => {
    const time = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
    const day =
      dayOffset === 0 || dayOffset === -1
        ? new Intl.RelativeTimeFormat(locale, {numeric: 'auto'}).format(dayOffset, 'day')
        : new Intl.DateTimeFormat(locale, {day: 'numeric', month: 'short'}).format(date);

    return `${day}, ${time}`;
  };

  try {
    return build(toDisplayLocale(language));
  } catch {
    return build(DISPLAY_LOCALE_FALLBACK);
  }
}

function resetTimeInDate(date?: string | Date) {
  if (!date) {
    return;
  }

  if (date && typeof (date === 'string' || date instanceof Date)) {
    const updatedDate = date instanceof Date ? date : new Date(date);
    updatedDate.setHours(0, 0, 0, 0);
    return updatedDate;
  }

  return new Date(date);
}

function formatShortTime(time?: string) {
  return time ? time.slice(0, 5) : undefined;
}

export {
  resetTimeInDate,
  formatDisplayDate,
  formatClockLabel,
  formatClockTime,
  formatDayAndTime,
  formatShortTime,
};
